import React, { useEffect, useId, useRef, useState } from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';

import { type LensCertificationAction, useActOnLensCertification } from '~/hooks/apis/reporting/useLensCertification';

import type { LensCertification, LensResponse } from '~/types/leLens';

const LABELS: Record<LensCertificationAction, string> = {
  request: 'Request certification',
  certify: 'Certify',
  decline: 'Decline certification',
  remove: 'Remove certification',
};

export function lensCertificationActions(
  access: LensResponse['access'],
  certification: LensCertification | undefined,
  companyReadable: boolean,
): Array<LensCertificationAction> {
  if (!certification) {
    return [];
  }
  const certified = certification.badge.kind === 'certified';
  return [
    access.canRequestCertification && !certified && certification.request.kind !== 'open' ? 'request' : null,
    access.canCertify && !certified ? 'certify' : null,
    access.canRemoveCertification && (certification.badge.kind !== 'none' || companyReadable) ? 'remove' : null,
  ].filter((action): action is LensCertificationAction => action !== null);
}

export default function LensCertificationMenuItems({
  lensId,
  actions,
  meaning,
  request,
  parentMenuOpen = true,
  onDone,
}: {
  lensId: string;
  actions: Array<LensCertificationAction>;
  meaning?: string;
  request?: LensCertification['request'];
  parentMenuOpen?: boolean;
  onDone: () => void;
}) {
  const act = useActOnLensCertification();
  const menuId = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const submenuPaperRef = useRef<HTMLDivElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const opensToLeft = Boolean(anchor && anchor.getBoundingClientRect().right + 272 > window.innerWidth - 16);
  const requestState =
    request?.kind === 'open'
      ? 'Certification requested'
      : request?.kind === 'declined'
        ? `Certification declined: ${request.note}`
        : null;

  const closeSubmenu = (restoreFocus = true) => {
    returnFocusRef.current = restoreFocus ? anchor : null;
    setAnchor(null);
  };

  useEffect(() => {
    if (!anchor) {
      return;
    }
    const closeOnOutsideClick = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target || anchor.contains(target) || submenuPaperRef.current?.contains(target)) {
        return;
      }
      returnFocusRef.current = null;
      setAnchor(null);
    };
    document.addEventListener('pointerdown', closeOnOutsideClick, true);
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick, true);
  }, [anchor]);

  useEffect(() => {
    if (!parentMenuOpen) {
      returnFocusRef.current = null;
      setAnchor(null);
    }
  }, [parentMenuOpen]);

  if (!actions.length && !requestState) {
    return null;
  }

  return (
    <>
      <MenuItem
        id={`${menuId}-trigger`}
        aria-controls={anchor && parentMenuOpen ? menuId : undefined}
        aria-expanded={Boolean(anchor && parentMenuOpen)}
        aria-haspopup="menu"
        disabled={act.isPending}
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(event.currentTarget);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            setAnchor(event.currentTarget);
          }
        }}
        onMouseEnter={(event) => {
          if (!act.isPending) {
            setAnchor(event.currentTarget);
          }
        }}
      >
        <ListItemIcon>
          <VerifiedOutlinedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Certification</ListItemText>
        <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary', ml: 1 }} />
      </MenuItem>
      <Menu
        anchorEl={anchor}
        anchorOrigin={{ vertical: 'top', horizontal: opensToLeft ? 'left' : 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: opensToLeft ? 'right' : 'left' }}
        disableRestoreFocus
        hideBackdrop
        id={menuId}
        onClose={(_event, reason) => closeSubmenu(reason === 'escapeKeyDown')}
        open={Boolean(anchor && parentMenuOpen)}
        slotProps={{
          transition: {
            onExited: () => {
              const trigger = returnFocusRef.current;
              returnFocusRef.current = null;
              if (parentMenuOpen && trigger?.isConnected) {
                trigger.focus();
              }
            },
          },
          list: {
            'aria-labelledby': `${menuId}-trigger`,
            onKeyDown: (event) => {
              event.stopPropagation();
              if (event.key === 'ArrowLeft' || event.key === 'Escape') {
                event.preventDefault();
                closeSubmenu();
              }
            },
          },
          paper: {
            ref: submenuPaperRef,
            onClick: (event) => event.stopPropagation(),
            sx: {
              pointerEvents: 'auto',
              maxWidth: 'calc(100vw - 24px)',
              p: 0.5,
              width: 272,
              '& .MuiListItemIcon-root': { color: 'text.secondary', minWidth: 34 },
              '& .MuiMenuItem-root': { borderRadius: 1, minHeight: 42 },
            },
          },
        }}
        sx={{ pointerEvents: 'none' }}
      >
        {requestState && (
          <MenuItem disabled sx={{ whiteSpace: 'normal' }}>
            {requestState}
          </MenuItem>
        )}
        {actions.map((action) => (
          <MenuItem
            key={action}
            disabled={act.isPending}
            onClick={async () => {
              closeSubmenu(false);
              onDone();
              await act
                .mutateAsync({ lensId, action, meaning: action === 'certify' ? meaning : undefined })
                .catch(() => undefined);
            }}
          >
            <ListItemIcon>
              <VerifiedOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {LABELS[action]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
